import { authManager } from "@/services/authManager";
import { fetchEventSource } from "@microsoft/fetch-event-source";

const IS_DEBUG = import.meta.env.DEV;
export const API_URL = import.meta.env.VITE_BACKEND_URL;
export const API_TIMEOUT_MS = 30_000;

const clientFetch = async (url, requestOptions) => {
  const { requiresAuth = true, ...options } = requestOptions;

  if (requiresAuth) {
    await authManager.wait();
  }

  const response = await fetch(url, { ...options, credentials: "include" });
  if (response.status === 401 && requiresAuth) {
    await authManager.refreshToken();
    try {
      return await fetch(url, { ...options, credentials: "include" });
    } catch (error) {
      return response;
    }
  }

  return response;
};

const processJsonResponse = async (response) => {
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: { message: `HTTP Error: ${response.status} ${response.statusText}` },
    }));

    if (IS_DEBUG && errorBody?.error) {
      const { message, details } = errorBody.error;
      console.error(`Server Error: ${message}`, details ? JSON.stringify(details) : "");
    }

    const error = new Error(errorBody.error?.message || "Request failed");
    error.details = errorBody.error?.details;
    error.status = response.status;
    throw error;
  }

  return response.json();
};

export const apiClient = async (path, requestOptions = {}) => {
  const url = API_URL + path;
  const { body, signal, ...restOptions } = requestOptions;
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timeoutId = setTimeout(abort, API_TIMEOUT_MS);
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });

  const headers = {
    Accept: "application/json",
    ...restOptions.headers,
  };

  let bodyJson = body;
  if (body !== undefined) {
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      bodyJson = body;
    } else if (typeof body === "object") {
      bodyJson = JSON.stringify(body);
      headers["Content-Type"] = "application/json";
    } else {
      headers["Content-Type"] = "application/json";
    }
  }

  const fetchOptions = {
    ...restOptions,
    body: bodyJson,
    headers,
    signal: controller.signal,
  };

  try {
    const response = await clientFetch(url, fetchOptions);
    return await processJsonResponse(response);
  } catch (error) {
    return { error: { message: error.message, details: error.details, status: error.status } };
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", abort);
  }
};

export const apiStreamClient = (path, { body, hooks }) => {
  const controller = new AbortController();
  const url = API_URL + path;

  fetchEventSource(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify(body),
    signal: controller.signal,
    fetch: clientFetch,
    openWhenHidden: true,
    ...hooks,
  });

  return controller;
};
